/**
 * Company utilities for handling dual company scenarios
 * (temporary employment agencies)
 */

import type { Activity, Company } from '../../../db/schema';

interface CompanyDisplayInfo {
  assignmentCompany: Company;
  tempAgency?: Company;
  isDualCompany: boolean;
}

interface ActivityWithTempAgency {
  id: number;
  companyId: number;
  tempAgencyId?: number;
}

/**
 * Get company display information for an activity
 * Handles both single company and dual company (temp agency) scenarios
 */
export function getCompanyDisplayInfo(
  activity: ActivityWithTempAgency,
  companies: Company[]
): CompanyDisplayInfo {
  const assignmentCompany = companies.find(c => c.id === activity.companyId);
  const tempAgency = activity.tempAgencyId 
    ? companies.find(c => c.id === activity.tempAgencyId)
    : undefined;

  if (!assignmentCompany) {
    throw new Error(`Assignment company not found for activity ${activity.id}`);
  }

  return {
    assignmentCompany,
    tempAgency,
    isDualCompany: !!tempAgency
  };
}

/**
 * Generate formatted company name for display
 * Returns "Assignment Company (via Temp Agency)" format when applicable
 */
export function getFormattedCompanyName(
  assignmentCompany: Company,
  tempAgency?: Company,
  showFullFormat = true
): string {
  if (!tempAgency || !showFullFormat) {
    return assignmentCompany.name;
  }
  
  return `${assignmentCompany.name} (via ${tempAgency.name})`;
}

/**
 * Check if an activity uses temporary employment
 */
export function isTempEmployment(activity: ActivityWithTempAgency): boolean {
  return !!activity.tempAgencyId;
}
